import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState, type ComponentType } from 'react';

import { Flex } from '@bonusly/design-system';

import { BizyEditConfirmation } from './bizy-edit-confirmation';

interface StreamingMessageProps {
	content: string;
	isComplete?: boolean;
}

interface BizyNotesEditMessageProps {
	/** The LLM's edit summary content */
	content: string;
	/** The streaming message component to use (differs between chat surfaces) */
	StreamingMessage: ComponentType<StreamingMessageProps>;
	/** Called when the delayed message text is revealed (for scroll-to-bottom) */
	onMessageReveal?: () => void;
}

/**
 * Staged notes edit message: chip slides in first, then message fades in after a delay.
 *
 * Creates a deliberate, phased feel instead of everything appearing at once.
 * - Phase 1 (0ms): Chip slides in with subtle entrance animation
 * - Phase 2 (300ms): Message text fades in below the chip
 */
export function BizyNotesEditMessage({ content, StreamingMessage, onMessageReveal }: BizyNotesEditMessageProps) {
	const [showMessage, setShowMessage] = useState(false);

	useEffect(() => {
		const timer = setTimeout(() => {
			setShowMessage(true);
			onMessageReveal?.();
		}, 300);
		return () => clearTimeout(timer);
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<Flex direction="column" gap={2}>
			<motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.15, ease: 'easeOut' }}>
				<BizyEditConfirmation />
			</motion.div>
			<AnimatePresence>
				{showMessage && (
					<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.2, ease: 'easeOut' }}>
						<StreamingMessage content={content} isComplete />
					</motion.div>
				)}
			</AnimatePresence>
		</Flex>
	);
}
